/**
 * Which cards can the list I am looking at actually contain?
 *
 * THE OBSERVATION. The filter vocabulary of a collection is the union of
 * every member card's elements, and the quick-filter chip row was built from
 * all of it. On a seventeen-card collection that is forty chips — "CDL class"
 * and "Trailer type" and "Obligation" offered above a list already narrowed to
 * Work orders, none of which can ever match anything on screen. The feature
 * got LESS useful exactly as a collection grew into what collections are for.
 *
 * THE ANSWER. The active filter already says which cards the list can return;
 * read it, and narrow the chip row to those. That derivation is this module,
 * and it is DELIBERATELY PURE — no React, no antd, no HTTP, the same
 * discipline as utils/facets.js — because what it answers is a question about
 * a query, not about a screen.
 *
 * WHAT IS NOT DECIDED HERE. Scope is a PRESENTATION narrowing and nothing
 * else. It never changes what a query means, never removes a condition, and
 * never narrows the filter builder's field menu — the builder is the door
 * that expresses anything, including a condition on the card you are about to
 * switch to. `utils/facets.js` decides what to do with the answer.
 *
 * TWO SOURCES, TWO HIERARCHY RULES, and getting them backwards makes the chip
 * row disagree with the list it describes:
 *
 * - `?card=<id>` is EXACTLY that card. useCollectionItems sends `card_id`
 *   with no `include_descendants`, so keepr-api's buildItemsQuery matches the
 *   one card.
 * - `card = <ref>` in `q` is that card AND ITS DESCENDANTS. keepr-api's
 *   compileCard runs Access.expandDescendantCardIds, because an item of a
 *   child card IS an item of its parent.
 *
 * And a card REF matches case-insensitively: the compiler builds its
 * vocabulary as `byKey.set(String(card.key).toLowerCase(), card)`. A
 * case-sensitive lookup here would resolve nothing, yield an empty scope, and
 * hide every chip while the list is full of items.
 */
import { parseForBuilder } from './kqlBuilder';
import { cardAndDescendantIds } from './facets';
import { systemFieldOf } from './kql';
import { normalizeId } from './ids';

/** The system-field root that names a card type. */
const CARD_ROOT = 'card';

/** 24 hex characters — the spelling a card is named by id in. */
const HEX_ID_RE = /^[0-9a-fA-F]{24}$/;

/**
 * UNRESTRICTED is `null`, and it is not the same as "nothing matches".
 *
 * An EMPTY Set is a real answer — `card is empty`, or two contradictory card
 * clauses — and it must render no scoped chips rather than falling open to
 * all of them. Every combinator below therefore tests for null explicitly and
 * never leans on truthiness, where an empty Set would read as "no answer".
 */
const UNRESTRICTED = null;

/**
 * Index of `cards` by key (lowercased) and by id — the compiler's own vocabulary.
 *
 * A CARD KEY IS NOT GLOBALLY UNIQUE. keepr-api's resolveCardKey de-duplicates
 * within ONE ownership scope (`{scope:'collection', collection_id}`,
 * `{scope:'global'}`, or the creator's `{owner}`), so two collections may each
 * own a card keyed `visit` — the default slug, and the showcase collections
 * repeat Note/Task/Person/Vehicle freely. `cards` here is the whole readable
 * catalogue (GET /api/card-definitions returns every card the account can
 * read, across every collection), while the API compiles `card = visit`
 * against ONE collection's vocabulary: loadQueryCards gathers that
 * collection's own cards, its embedded members, every global, and the
 * ancestors those inherit from — and nothing else.
 *
 * So the key index is TIERED, in the API's FIXED ORDER (deterministic
 * filters, ruling E1; keepr-api utils/kql/resolveNames.js — what a typed key
 * resolves to, for a schema reader's list and for every writer):
 *
 *   0. the collection's own card,
 *   1. its ancestors' (a sub-collection's records are made of them),
 *   2. its sub-collections' (a parent's list shows their records),
 *   3. its members (`memberCardIds`, already narrowed to a card-scoped
 *      user's grants),
 *   4. the global cards,
 *   5. a card a collection link lends the list (`borrowedIds`),
 *   6. any other readable card (a legacy ancestor a member inherits from).
 *
 * The best tier wins; within one, the last declaration (two cards of one tier
 * share a key only in legacy data). Without `collectionId` the first three
 * tiers are empty, and members still beat everything else — the disagreement
 * that actually bites. A list's `q` is soon the server's own canonical text,
 * ids and all (X-Query-Canonical), so this matters for the moment before
 * that answer, and for a reader the server tells nothing.
 *
 * `byId` stays flat: an id names exactly one card everywhere.
 */
function cardVocabulary(cards, memberIds = null, { collectionId = null, ancestorIds = null, descendantIds = null, borrowedIds = null } = {}) {
	const own = normalizeId(collectionId);
	const ancestors = asIdSet(ancestorIds);
	const descendants = asIdSet(descendantIds);
	const borrowed = asIdSet(borrowedIds);
	const tierOf = (card, id) => {
		const home = normalizeId(card?.collection_id);
		if (own && home === own) return 0;
		if (home && ancestors.has(home)) return 1;
		if (home && descendants.has(home)) return 2;
		if (memberIds instanceof Set && memberIds.has(id) && !borrowed.has(id)) return 3;
		if (card?.scope === 'global') return 4;
		if (borrowed.has(id)) return 5;
		return 6;
	};
	const byKey = new Map(); // key -> { card, tier }
	const byId = new Map();
	(cards || []).forEach(card => {
		const id = normalizeId(card?._id);
		if (!id) return;
		byId.set(id, card);
		const key = typeof card?.key === 'string' ? card.key.trim().toLowerCase() : '';
		if (!key) return;
		const tier = tierOf(card, id);
		const held = byKey.get(key);
		// The best tier wins; within one, the last declaration.
		if (!held || tier <= held.tier) byKey.set(key, { card, tier });
	});
	return { byKey, byId };
}

const asIdSet = ids => new Set((ids instanceof Set ? [...ids] : (Array.isArray(ids) ? ids : []))
	.map(entry => normalizeId(entry?._id ?? entry))
	.filter(Boolean));

/**
 * The card a ref names — a 24-hex id, or a key in the fixed order above —
 * or null. The resolver the chips and the scope share, so a key means the
 * same card in both.
 */
export function createCardResolver(cards, options = {}) {
	const memberIds = asMemberIds(options.memberCardIds);
	const vocab = cardVocabulary(cards, memberIds, options);
	return ref => {
		const text = String(ref ?? '').trim();
		if (!text) return null;
		if (HEX_ID_RE.test(text)) return vocab.byId.get(normalizeId(text)) || null;
		return vocab.byKey.get(text.toLowerCase())?.card || null;
	};
}

const asMemberIds = memberCardIds => (memberCardIds instanceof Set
	? memberCardIds
	: (Array.isArray(memberCardIds) ? new Set(memberCardIds.map(normalizeId).filter(Boolean)) : null));

/** Every raw string a value node names: one for a scalar, many for a list. */
function refsOf(value) {
	if (!value || typeof value !== 'object') return [];
	if (value.type === 'list') return (value.v || []).flatMap(refsOf);
	// `me`, a relative date, a number — nothing that names a card.
	if (value.type !== 'str') return [];
	const text = String(value.v ?? '').trim();
	return text ? [text] : [];
}

/**
 * The card family a ref names: the card it resolves to plus every descendant,
 * because `card = vehicle` returns trailers too. A ref that resolves to
 * nothing contributes nothing — the same no-match the API compiles it to.
 *
 * The descendant walk runs over the FULL `cards` list on purpose: the
 * hierarchy that decides what "or a descendant" means is not the collection's
 * to narrow. Only the key -> card step is collection-first (cardVocabulary).
 */
function familyOf(ref, cards, vocab) {
	const text = String(ref).trim();
	const key = text.toLowerCase();
	const card = HEX_ID_RE.test(text)
		? vocab.byId.get(normalizeId(text))
		: vocab.byKey.get(key)?.card;
	const id = normalizeId(card?._id);
	if (!id) return [];
	return cardAndDescendantIds(cards, id);
}

/** Is this node a condition on the CARD root (and not on an element named `card`)? */
const isCardCondition = node => (
	node?.op === 'cmp' && Array.isArray(node.path) && systemFieldOf(node) === CARD_ROOT
);

/** a ∩ b, with null as the identity ("this side says nothing"). */
function intersect(a, b) {
	if (a === UNRESTRICTED) return b;
	if (b === UNRESTRICTED) return a;
	return new Set([...a].filter(id => b.has(id)));
}

/**
 * a ∪ b, with null ABSORBING: a branch of an `or` that says nothing about
 * cards can return any card at all, so the whole `or` is unrestricted. The
 * asymmetry with `and` is the whole of the logic — `card = visit or status =
 * open` is not a visit list.
 */
function union(a, b) {
	if (a === UNRESTRICTED || b === UNRESTRICTED) return UNRESTRICTED;
	return new Set([...a, ...b]);
}

/** The complement of a set within the collection's membership, or null when we have no membership to subtract from. */
function complement(memberIds, scope) {
	if (scope === UNRESTRICTED) return UNRESTRICTED;
	if (!(memberIds instanceof Set) || !memberIds.size) return UNRESTRICTED;
	return new Set([...memberIds].filter(id => !scope.has(id)));
}

/** The scope one `card <cmp> <value>` condition contributes. */
function scopeOfCardCondition(node, { cards, vocab, memberIds }) {
	const families = () => new Set(refsOf(node.value).flatMap(ref => familyOf(ref, cards, vocab)));
	switch (node.cmp) {
		case 'eq':
		case 'in':
			return families();
		case 'ne':
		case 'nin':
			return complement(memberIds, families());
		// Every item has a card, so `card is empty` is the contradiction; the
		// honest answer is the empty set, not "everything".
		case 'empty':
			return new Set();
		case 'nempty':
			return UNRESTRICTED;
		// `card ~ visit` and the ordering comparisons have no membership
		// reading the chip row could trust. Say nothing.
		default:
			return UNRESTRICTED;
	}
}

/**
 * Is every condition under this node a card condition? The precondition for
 * negating a whole subtree: the scope of a mixed subtree is an over-estimate
 * (it ignores the non-card clauses), and the complement of an over-estimate
 * is an UNDER-estimate — chips silently missing from a list that has items.
 */
function isPureCardSubtree(node) {
	if (!node || typeof node !== 'object') return false;
	if (node.op === 'and' || node.op === 'or') {
		const args = node.args || [];
		return args.length > 0 && args.every(isPureCardSubtree);
	}
	if (node.op === 'not') return isPureCardSubtree(node.arg);
	return isCardCondition(node);
}

/** The scope an arbitrary AST node contributes. */
function scopeOfNode(node, ctx) {
	if (!node || typeof node !== 'object') return UNRESTRICTED;
	if (node.op === 'and') {
		return (node.args || []).reduce((acc, arg) => intersect(acc, scopeOfNode(arg, ctx)), UNRESTRICTED);
	}
	if (node.op === 'or') {
		const args = node.args || [];
		if (!args.length) return UNRESTRICTED;
		return args.reduce((acc, arg, index) => (
			index === 0 ? scopeOfNode(arg, ctx) : union(acc, scopeOfNode(arg, ctx))
		), UNRESTRICTED);
	}
	// `not (card = visit)` subtracts, exactly as `card != visit` does — but
	// ONLY when every leaf under it is a card condition. De Morgan holds
	// there and nowhere else: `not (card = visit and status = open)` still
	// returns visits, the ones that are not open, so negating the card scope
	// would delete the chips that list most needs. A negation we cannot read
	// says nothing, which is the safe direction.
	if (node.op === 'not') {
		if (!isPureCardSubtree(node.arg)) return UNRESTRICTED;
		return complement(ctx.memberIds, scopeOfNode(node.arg, ctx));
	}
	if (isCardCondition(node)) return scopeOfCardCondition(node, ctx);
	return UNRESTRICTED;
}

/**
 * cardIdsInScope({ cards, memberCardIds, cardFilter, query })
 *   -> { ids: Set<string> | null, source: 'card-param' | 'query' | 'none' }
 *
 * `ids` null means UNRESTRICTED — the filter says nothing about cards, so
 * every card in the collection is still on the table. An EMPTY Set is a real
 * answer and means no card can match; a caller must render no scoped chips
 * rather than falling open.
 *
 * `cards` is the full readable card list (the hierarchy that decides what "or
 * a descendant" means is not the collection's to narrow); `memberCardIds` is
 * the collection's own membership, which is what a negation subtracts from
 * AND which card a bare key names when two collections own the same key
 * (cardVocabulary).
 * `cardFilter` is the `?card=` param, `query` the raw KQL text — an
 * unparseable one restricts nothing, which is the safe direction.
 *
 * `source` names which half produced the restriction, for a caller that wants
 * to explain itself; when both do, the param wins the label, being the more
 * specific of the two.
 */
export function cardIdsInScope({
	cards = [], memberCardIds = null, cardFilter = null, query = '',
	collectionId = null, ancestorIds = null, descendantIds = null, borrowedIds = null,
} = {}) {
	const memberIds = asMemberIds(memberCardIds);
	// The tiers decide which card a KEY names (see cardVocabulary).
	const vocab = cardVocabulary(cards, memberIds, { collectionId, ancestorIds, descendantIds, borrowedIds });
	const ctx = { cards, vocab, memberIds };

	// The `q` half. parseForBuilder never throws: a query the parser refuses
	// is a query we know nothing about, and knowing nothing is unrestricted.
	const { ast } = parseForBuilder(query);
	const fromQuery = scopeOfNode(ast, ctx);

	// The `?card=` half — EXACTLY that card, no descendants, because the feed
	// sends `card_id` without `include_descendants`.
	const paramId = normalizeId(cardFilter);
	const fromParam = paramId ? new Set([paramId]) : UNRESTRICTED;

	const ids = intersect(fromParam, fromQuery);
	const source = fromParam !== UNRESTRICTED ? 'card-param' : (fromQuery !== UNRESTRICTED ? 'query' : 'none');
	return { ids, source };
}

export default cardIdsInScope;
